import type { HTMLAttributes, ReactNode } from 'react'

import { cn } from './cn'

/**
 * 卡片。白底、1px 描边、14px 圆角、极轻的一层阴影。
 *
 * `tone` 是设计稿里那几种带色的卡：
 *   plain  白底。绝大多数
 *   warm   微暖底。次级面板
 *   green  浅绿。「我猜这台是」这类肯定语气的
 *   amber  琥珀。「等你看一眼」
 *   red    红。碰钱碰数据的授权卡
 *   dark   深色。凭证卡、代码块、「正在跑的活」
 */
export type CardTone = 'plain' | 'warm' | 'green' | 'amber' | 'red' | 'dark'
export type CardPadding = 'sm' | 'md' | 'lg' | 'none'

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  tone?: CardTone
  padding?: CardPadding
  children?: ReactNode
}

const tones: Record<CardTone, string> = {
  plain: 'bg-npc-panel border border-npc-border text-npc-ink',
  warm: 'bg-npc-surface-warm border border-npc-border text-npc-ink',
  green: 'bg-npc-green-bg border border-npc-green-border text-npc-green-deep',
  amber: 'bg-npc-amber-bg border border-npc-amber-border text-npc-amber-ink',
  red: 'bg-npc-red-bg border border-npc-red-border text-npc-red-ink',
  dark: 'bg-npc-dark border border-npc-dark-border text-npc-dark-ink',
}

const paddings: Record<CardPadding, string> = {
  none: '',
  sm: 'p-npc-card-sm',
  md: 'p-npc-card',
  lg: 'p-npc-card-lg',
}

export function Card({ tone = 'plain', padding = 'md', className, ...rest }: CardProps) {
  return (
    <div
      className={cn('rounded-npc-card shadow-npc-card', tones[tone], paddings[padding], className)}
      {...rest}
    />
  )
}

/** 卡片标题。13.5px / 600，与设计稿一致。 */
export function CardTitle({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('text-npc-card-title font-semibold', className)} {...rest} />
}

/** 卡片里的说明文字。 */
export function CardHint({ className, ...rest }: HTMLAttributes<HTMLParagraphElement>) {
  return <p className={cn('text-npc-caption text-npc-ink-soft m-0', className)} {...rest} />
}
